import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { subscribePopup, closePopup } from '../lib/popup';

/**
 * The shared "opens in a pop-up" overlay (see lib/popup.js). Mounted once, near the top
 * of the page, so it always sits above every section regardless of which one opened it.
 * Shows the linked page (currently: the Zeffy donation form) in an iframe, over a dimmed
 * backdrop, without leaving the site.
 */
export default function PopupFrame() {
  const [state, setState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => subscribePopup(setState), []);

  useEffect(() => {
    if (!state) return undefined;
    setLoaded(false);
    const onKey = (e) => { if (e.key === 'Escape') closePopup(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current && closeRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [state]);

  if (!state) return null;

  return createPortal(
    <div className="popup-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
      <div className="popup-card" role="dialog" aria-modal="true" aria-label={state.label || 'Donate'}>
        <button ref={closeRef} type="button" className="popup-close" onClick={closePopup} aria-label="Close">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M6.4 4.98 4.98 6.4 10.59 12l-5.61 5.6 1.42 1.42L12 13.41l5.6 5.61 1.42-1.42L13.41 12l5.61-5.6-1.42-1.42L12 10.59Z"/></svg>
        </button>
        {!loaded && (
          <div className="popup-loading" aria-hidden="true">
            <span className="popup-spinner" />
          </div>
        )}
        <iframe
          title={state.label || 'Donation form'}
          src={state.url}
          className="popup-iframe"
          allow="payment"
          onLoad={() => setLoaded(true)}
        />
        <p className="popup-fallback">
          Form not loading? <a href={state.url} target="_blank" rel="noopener noreferrer">Open it in a new tab</a> instead.
        </p>
      </div>
    </div>,
    document.body
  );
}
